import * as React from "react";
import { toast } from "sonner";
import { Copy, Info } from "lucide-react";
import { Alert, Button } from "@/components/ui";
import { Destination } from "../useFormRouteBuilder";
import { copyText } from "./RouteOutput";
import { RouteCard } from "./RouteCard";

const STEPS: Record<Destination, { title: string; steps: string[] }> = {
  totalexpert: {
    title: "Put it in TotalExpert",
    steps: [
      "Copy the route above.",
      "In the journey email, select the button or text that should open the form and paste the route as its link.",
      "Keep the merge fields exactly as they are. TotalExpert replaces them for each recipient when the email is sent.",
      "Send yourself a test from TotalExpert and click the link. The Form Links tab shows the link it created, marked via TotalExpert route.",
    ],
  },
  crm: {
    title: "Put it in your CRM",
    steps: [
      "Copy the route above.",
      "Paste it as the link on a button or text in your email template.",
      "Your CRM replaces the merge fields for each recipient at send time.",
      "Send yourself a test and click it. The link it created appears in Form Links.",
    ],
  },
  website: {
    title: "Put it on your website",
    steps: [
      "Render the route on the page with the real values for this client, or at least the player.",
      "Without a client, the form asks for their details. Without a player, it opens at the account level.",
      "Use it on a loan officer's profile page, a client portal, or a post-close thank-you page.",
    ],
  },
};

export function RouteHowTo({ destination, url }: { destination: Destination; url: string | null }) {
  const { title, steps } = STEPS[destination];
  const snippet = `<a href="${url ?? ""}">Share your feedback</a>`;

  return (
    <RouteCard title={title}>
      <ol className="flex flex-col gap-2.5">
        {steps.map((step, index) => (
          <li key={index} className="flex gap-3 text-sm text-slate-700 leading-relaxed">
            <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-slate-100 text-[11px] font-bold text-slate-600">
              {index + 1}
            </span>
            <span>{step}</span>
          </li>
        ))}
      </ol>
      {destination === "totalexpert" && (
        <Alert className="bg-blue-50/50 border-blue-100 text-blue-900">
          <Info />
          <Alert.Description className="text-blue-900">
            Merge-field names vary by TotalExpert setup. If a value shows up blank in Form Links, fix the name in the
            table above and paste the route again.
          </Alert.Description>
        </Alert>
      )}
      {destination === "website" && url && (
        <div className="relative rounded-xl bg-slate-900 p-4 pr-24">
          <code className="font-mono text-xs text-slate-100 break-all" data-testid="website-snippet">
            {snippet}
          </code>
          <Button
            size="xs"
            variant="secondary"
            className="absolute right-3 top-3"
            onClick={async () => {
              await copyText(snippet);
              toast.success("Snippet copied");
            }}
          >
            <Copy className="mr-1 h-3 w-3" />
            Copy
          </Button>
        </div>
      )}
    </RouteCard>
  );
}
